/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * eventReducer.ts — even\1 \2educer (services/operator-web-app).
 */

import type { ServerEnvelope } from '@/realtime/types';
import type { PresenceState } from './slices/presenceSlice';
import type { CallState } from './slices/callSlice';
import type { WorkspaceState } from './slices/workspaceSlice';
import type { CopilotState } from './slices/copilotSlice';
import { initialPresenceState, reducePresence } from './slices/presenceSlice';
import { initialCallState, reduceCall } from './slices/callSlice';
import { initialWorkspaceState, reduceWorkspace } from './slices/workspaceSlice';
import { initialCopilotState, reduceCopilot } from './slices/copilotSlice';

export interface OperatorState {
  presence: PresenceState;
  call: CallState;
  workspace: WorkspaceState;
  copilot: CopilotState;
  /** E3-S3: Local UI state for in-call Mute (resets on call_offered / call_ended). */
  callMuted: boolean;
  /** E3-S3: Local UI state for in-call Hold (resets on call_offered / call_ended). */
  callHeld: boolean;
  /** E3-S3 T3: Voice media connection state (updated by useVoiceConnection). */
  voiceConnectionState: 'disconnected' | 'connecting' | 'starting' | 'active' | 'ended';
  /** Idempotency: skip event if already applied */
  processedEventIds: Set<string>;
  /** Max size of processedEventIds to avoid unbounded growth */
  maxProcessedEventIds: number;
  /** Last time we received any server message (for degraded connection detection) */
  lastServerActivityAt: number | null;
}

const PRESENCE_EVENTS = new Set(['presence_snapshot', 'presence_status_changed']);
const CALL_EVENTS = new Set(['call_offered', 'call_assigned', 'call_connected', 'call_ended']);
const WORKSPACE_EVENTS = new Set([
  'customer_context_loaded',
  'meeting_conflict',
  'address_validation_warning',
  'wrap_up_required',
]);
const COPILOT_EVENTS = new Set([
  'asr_partial',
  'asr_final',
  'hint_generated',
  'highlight_recommended',
]);

const MAX_PROCESSED_IDS = 2000;

export function applyEvent(state: OperatorState, envelope: ServerEnvelope): OperatorState {
  const { event_id, event_type, payload, call_id, session_id } = envelope;
  if (state.processedEventIds.has(event_id)) return state;

  const nextProcessed = new Set(state.processedEventIds);
  nextProcessed.add(event_id);
  if (nextProcessed.size > state.maxProcessedEventIds) {
    const arr = Array.from(nextProcessed);
    arr.splice(0, arr.length - state.maxProcessedEventIds);
    nextProcessed.clear();
    arr.forEach((id) => nextProcessed.add(id));
  }

  let presence = state.presence;
  let call = state.call;
  let workspace = state.workspace;
  let copilot = state.copilot;

  if (PRESENCE_EVENTS.has(event_type)) {
    presence = reducePresence(presence, event_type, payload, event_id);
  }
  if (CALL_EVENTS.has(event_type)) {
    call = reduceCall(call, event_type, payload, event_id, call_id, session_id);
  }
  if (WORKSPACE_EVENTS.has(event_type)) {
    workspace = reduceWorkspace(workspace, event_type, payload, event_id);
  }
  if (COPILOT_EVENTS.has(event_type)) {
    copilot = reduceCopilot(copilot, event_type, payload, event_id);
  }

  return {
    ...state,
    presence,
    call,
    workspace,
    copilot,
    processedEventIds: nextProcessed,
    lastServerActivityAt: Date.now(),
  };
}

export function getInitialOperatorState(maxProcessedEventIds = MAX_PROCESSED_IDS): OperatorState {
  return {
    presence: initialPresenceState,
    call: initialCallState,
    workspace: initialWorkspaceState,
    copilot: initialCopilotState,
    callMuted: false,
    callHeld: false,
    voiceConnectionState: 'disconnected',
    processedEventIds: new Set(),
    maxProcessedEventIds,
    lastServerActivityAt: null,
  };
}
